import React from 'react';
import { Check } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { ADMIN_NAV } from '@/components/admin/adminNav';

/**
 * Selector panel for the dashboard's Quick Access tiles — every admin area,
 * grouped, with the current selection ticked. Ticking or unticking saves
 * straight away, so the grid behind updates as the admin works.
 */
export default function QuickAccessCustomizer({ open, onClose, selected, onToggle, max }) {
  const atCapacity = selected.length >= max;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="max-w-2xl bg-[#0d0d0f] border-white/10">
        <DialogHeader>
          <DialogTitle className="text-white text-sm font-bold tracking-widest uppercase">
            Customize Quick Access
          </DialogTitle>
          <DialogDescription className="text-white/40 text-xs">
            Tick the admin areas you want on your dashboard. {selected.length} of {max} selected — your layout is
            saved to your login and restored every time you sign in.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto pr-1 space-y-5">
          {ADMIN_NAV.map((section) => (
            <div key={section.group}>
              <p className="text-white/30 text-[10px] font-bold tracking-widest mb-2">
                {section.group.toUpperCase()}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {section.items.map((item) => {
                  const isSelected = selected.includes(item.to);
                  const disabled = !isSelected && atCapacity;
                  return (
                    <button
                      key={item.to}
                      type="button"
                      disabled={disabled}
                      onClick={() => onToggle(item.to)}
                      className={`flex items-center gap-2.5 border px-3 py-2.5 text-left transition-colors ${
                        isSelected ? 'border-primary/40 bg-primary/5' : 'border-white/10 hover:border-white/25'
                      } ${disabled ? 'opacity-35 cursor-not-allowed' : ''}`}
                    >
                      <span
                        className={`flex items-center justify-center w-4 h-4 border shrink-0 ${
                          isSelected ? 'border-primary bg-primary' : 'border-white/25'
                        }`}
                      >
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </span>
                      <item.icon className={`w-4 h-4 shrink-0 ${isSelected ? 'text-primary' : 'text-white/40'}`} />
                      <span className={`text-xs font-bold tracking-wider ${isSelected ? 'text-white' : 'text-white/60'}`}>
                        {item.label.toUpperCase()}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between gap-3 pt-3 border-t border-white/10">
          <p className="text-white/30 text-[10px] font-bold tracking-widest">
            {atCapacity ? `MAXIMUM ${max} TILES REACHED` : `${max - selected.length} SLOTS FREE`}
          </p>
          <button
            onClick={onClose}
            className="bg-primary hover:bg-primary/90 text-white text-xs font-bold tracking-widest px-5 py-2.5 transition-colors"
          >
            DONE
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}